// Keyboard shortcuts that insert a content block on the current slide.
//
// One table, used by both the key handler in LessonTemplate and the reference
// list in HotkeysModal. The modal's other groups are hand-copied from their
// handlers and carry a comment begging whoever changes one to change the other;
// these cannot drift, because there is only one list.
//
// **Ctrl+Alt for most of them.** A deck is edited in a browser tab, and the
// plain and shifted Ctrl rows are largely spoken for: Ctrl+T/N/W/L/D/F/P
// belong to the browser, Ctrl+Shift+T/N/P/W/I/J likewise, and Ctrl+A/C/X/V/Z/G
// are already the Slide Designer's. Windows itself only reserves
// Ctrl+Alt+Delete, so that row is the one a page can generally take.
//
// **Generally — but not always, and that is why an entry may name its own.**
// On Windows, Ctrl+Alt *is* AltGr. The page usually still sees the keypress,
// and the three readings in `matchesLetter` below cover the layouts that
// rewrite the character. What none of that can survive is the combination
// being consumed before the page is reached at all — by the keyboard layout,
// an IME, an extension that has registered the same command with the browser,
// or a utility holding a global hotkey. Nothing in a page can win that fight,
// and no amount of matching helps: the event never arrives.
//
// **Which is why Custom HTML/CSS answers to two chords.** It was reported not
// working on Chrome for Windows on Ctrl+Alt+M, moved to Ctrl+Shift+L, and
// reported not working again. Driven through the real editor with real key
// events, both chords open the dialog on the right slide — so the code is not
// what is failing, the delivery is, and *which* chord a given machine eats is
// not something this file can know. Ctrl+Shift+L is the default autofill
// command of more than one popular password manager (Bitwarden and LastPass
// both ship it), and an extension command is registered with the browser: it
// fires whatever a page does, and the page never sees the key. Ctrl+Alt+M is
// AltGr+M, which some layouts and IMEs swallow instead.
//
// The two failure modes are unrelated, so a machine that eats one will
// generally still deliver the other. Listing both costs one letter of key
// space and removes the guessing: whichever arrives, the dialog opens. Both
// are printed in the shortcut list, so nothing is advertised that the handler
// does not answer.
//
// Letters are mnemonic where the obvious letter was free. Title takes H, for
// heading, because Text has the better claim on T; Citation takes R, for
// reference. Custom HTML/CSS takes **L for layout** — the app's own word for
// the block, in the toolbar ("Lay this slide out with your own HTML and CSS")
// and in the file it exports (`lessonplanner-layout-*.html`) — and keeps M,
// for markup, as its second chord. Reference List has no shortcut on purpose:
// it goes in once per deck, so it would be spending a key to save a visit to a
// menu.
//
// S is reserved and must stay out of the Ctrl+Alt row: Ctrl+Alt+S is Save +.
// It sits there rather than on Ctrl+Shift+S because that combination is a
// common extension shortcut (Raindrop's save-bookmark, for one), and an
// extension command is registered with the browser — a page cannot take it
// back, it can only add a second thing that happens on the same keypress.

/** The modifier rows an entry can sit in. */
export const CHORD = {
  CTRL_ALT: 'ctrl-alt',
  CTRL_SHIFT: 'ctrl-shift'
};

export const BLOCK_SHORTCUTS = [
  { letter: 'T', action: 'text', label: 'Text block' },
  { letter: 'H', action: 'headline', label: 'Title' },
  { letter: 'L', action: 'list', label: 'List' },
  { letter: 'I', action: 'image', label: 'Image' },
  { letter: 'V', action: 'video', label: 'Video' },
  { letter: 'A', action: 'audio', label: 'Audio' },
  { letter: 'C', action: 'cards', label: 'Cards' },
  { letter: 'D', action: 'divider', label: 'Divider' },
  { letter: 'K', action: 'link', label: 'Link' },
  { letter: 'Q', action: 'apa-quote', label: 'APA quote' },
  { letter: 'R', action: 'apa-citation', label: 'Citation' },
  // Opens the same dialog the Insert drawer's HTML/CSS button opens, on the
  // same slide, at the same insert position — it is the same command. Two
  // chords, for the reason in the header: neither row is reliably delivered on
  // every machine, and they fail for unrelated reasons.
  {
    action: 'custom-html',
    label: 'Custom HTML/CSS',
    chords: [
      { chord: CHORD.CTRL_SHIFT, letter: 'L' },
      { chord: CHORD.CTRL_ALT, letter: 'M' }
    ]
  }
];

/**
 * Every chord an entry answers to, in the order the shortcut list prints them.
 *
 * The single-chord shorthand (`letter`, optionally with `chord`) is what most
 * entries use; `chords` is the long form for one that needs more than one.
 */
export const chordsFor = (shortcut) => (
  shortcut.chords || [{ chord: shortcut.chord || CHORD.CTRL_ALT, letter: shortcut.letter }]
);

/** Which row this event is in, or null if it is in neither. */
const chordOfEvent = (event) => {
  if (!(event.ctrlKey || event.metaKey)) return null;
  if (event.altKey && !event.shiftKey) return CHORD.CTRL_ALT;
  if (event.shiftKey && !event.altKey) return CHORD.CTRL_SHIFT;
  // Both or neither: Ctrl+Alt+Shift stays free for whatever wants it next, and
  // plain Ctrl belongs to the Slide Designer and the browser.
  return null;
};

/** How the shortcut list should print one chord. */
export const comboFor = ({ chord, letter }) => (
  chord === CHORD.CTRL_SHIFT ? ['Ctrl', 'Shift', letter] : ['Ctrl', 'Alt', letter]
);

/**
 * Whether this event is the given letter, by any of the three readings.
 *
 * One keypress, three ways to name it, and no single one of them is reliable
 * on every machine:
 *
 * - **`code`** is the physical key, so it survives Ctrl+Alt being AltGr on
 *   Windows and on European layouts, where `key` arrives as whatever character
 *   AltGr produces. It is the best answer when it is there — and it is not
 *   always there: a remote desktop session, an on-screen or virtual keyboard,
 *   and some IMEs deliver keydown with `code` empty.
 * - **`key`** keeps the mnemonics honest on a layout whose letters sit
 *   somewhere other than US positions. But AltGr transforms it: Ctrl+Alt+M on
 *   a US-International or Canadian Multilingual layout can arrive as `'µ'`,
 *   whose uppercase is GREEK CAPITAL MU — not the ASCII `'M'` this compares
 *   against. With `code` also missing, the shortcut then matches nothing and
 *   the key appears simply not to work.
 * - **`keyCode`** is deprecated and, for letters, is exactly the reason to
 *   keep it: it reports the ASCII code of the unmodified letter whatever the
 *   layout does to the character, and every engine still populates it.
 *
 * Matching any of them is safe. All three describe the *same* keypress, and
 * the modifier check in `findBlockShortcut` has already narrowed this to a
 * chord no plain typing produces.
 */
const matchesLetter = (event, letter) => {
  if (!letter) return false;
  if (event.code === `Key${letter}`) return true;
  if (event.key?.toUpperCase?.() === letter) return true;
  const code = event.keyCode ?? event.which;
  return typeof code === 'number' && code === letter.charCodeAt(0);
};

/**
 * The block shortcut this event asks for, or null.
 *
 * An entry matches only in a row it actually claims, so the same letter can
 * mean different things in each: Ctrl+Alt+L inserts a List, Ctrl+Shift+L the
 * Custom HTML/CSS layout.
 */
export const findBlockShortcut = (event) => {
  if (!event) return null;
  const row = chordOfEvent(event);
  if (!row) return null;
  return BLOCK_SHORTCUTS.find((shortcut) => chordsFor(shortcut).some(
    (entry) => entry.chord === row && matchesLetter(event, entry.letter)
  )) || null;
};

export default BLOCK_SHORTCUTS;
